import { LogoMark } from '../components/LogoMark.js';
import { ParticleField } from '../components/ParticleField.js';
import { IncenseSmoke } from '../components/IncenseSmoke.js';
import { ScrollIndicator } from '../components/ScrollIndicator.js';
import { useParallax } from '../hooks/useParallax.js';
import { Logo } from '@/components/Logo.js';
import { preferredStoreUrl } from '@/lib/appStores.js';

export function Hero() {
  const phoneRef = useParallax<HTMLDivElement>(0.18);
  const glowRef = useParallax<HTMLDivElement>(0.25);

  return (
    <section
      id="hero"
      className="ow-hero relative w-full overflow-hidden flex items-center"
      style={{
        minHeight: '100svh',
        height: 'auto',
        paddingTop: 96,
        paddingBottom: 48,
        background:
          'radial-gradient(circle at 20% 50%, var(--ow-bg) 0%, color-mix(in srgb, var(--ow-bg) 96%, #000 4%) 100%)',
      }}
    >
      {/* Incense smoke rising in the background. In dark mode a real footage
          plume glows via screen-blend; the CSS plumes are the light-mode
          fallback. Both are decorative and hidden under reduced motion. */}
      <video
        className="ow-incense-video"
        aria-hidden="true"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster="/video/incense-poster.jpg"
        ref={(el) => {
          // A slower, calmer rise than the source footage.
          if (el) el.playbackRate = 0.62;
        }}
      >
        <source src="/video/incense.webm" type="video/webm" />
        <source src="/video/incense.mp4" type="video/mp4" />
      </video>
      <IncenseSmoke count={12} />

      {/* Aureole - anchored to right side, behind the mockup */}
      <div
        ref={glowRef}
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          width: 1100,
          height: 1100,
          right: '-15%',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'radial-gradient(closest-side, var(--ow-gold-glow), transparent 70%)',
          opacity: 0.95,
          willChange: 'transform',
        }}
      />

      {/* Particles drifting upward across the whole hero */}
      <ParticleField count={22} />

      {/* Vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, transparent 60%, color-mix(in srgb, var(--ow-text-primary) 8%, transparent) 100%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-container px-5 md:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT - text */}
          <div className="flex flex-col">
            {/* Small logo + brand line, eyebrow row. The logo is hidden on
                mobile to avoid stacking under the Nav's brand mark. */}
            <div className="ow-fade-up flex items-center gap-3" style={{ animationDelay: '900ms' }}>
              <span className="text-gold hidden sm:inline-flex">
                <Logo size={22} />
              </span>
              <span
                className="font-display uppercase text-gold"
                style={{ fontSize: 11, letterSpacing: '0.32em' }}
              >
                The Daily Rhythm of Faith
              </span>
            </div>

            <h1
              className="ow-fade-up font-display text-primary mt-6"
              style={{
                fontSize: 'clamp(44px, 7vw, 88px)',
                fontWeight: 500,
                letterSpacing: '0.02em',
                lineHeight: 1.05,
                animationDelay: '1200ms',
              }}
            >
              Orthodox Way
            </h1>

            <p
              className="ow-fade-up scripture text-secondary mt-6 max-w-[520px]"
              style={{
                fontSize: 'clamp(20px, 2.2vw, 26px)',
                lineHeight: 1.5,
                animationDelay: '1500ms',
              }}
            >
              “A small, quiet room you walk into before the world gets loud.”
            </p>

            <p
              className="ow-fade-up text-secondary mt-5 max-w-[480px]"
              style={{ fontSize: 17, lineHeight: 1.7, animationDelay: '1800ms' }}
            >
              Prayer. Fasting. Liturgy. Saints. Scripture. The complete Orthodox life, reverently,
              on every device.
            </p>

            <div
              className="ow-fade-up mt-9 flex flex-wrap items-center gap-4"
              style={{ animationDelay: '2100ms' }}
            >
              {/* One CTA; the store resolves per device (iOS → App Store,
                  otherwise Google Play). Both stores are listed explicitly
                  in the final CTA section. */}
              <a
                href={preferredStoreUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="ow-cta-primary"
              >
                Get the App
              </a>
              <a href="#manifesto" className="ow-cta-ghost">
                See the Way
              </a>
            </div>
          </div>

          {/* RIGHT - animated cross. Hidden on mobile because at narrow
              widths the parallax-shifted mark drifts past the section
              boundary, looking cut off; the Nav's brand mark is enough. */}
          <div
            ref={phoneRef}
            className="hidden md:flex relative justify-center lg:justify-end"
            style={{ willChange: 'transform' }}
          >
            <a
              href="#manifesto"
              aria-label="See the Way"
              className="ow-hero-cross ow-focusable inline-block rounded-full"
            >
              <div className="hidden md:block lg:hidden">
                <LogoMark size={320} />
              </div>
              <div className="hidden lg:block">
                <LogoMark size={400} />
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Centered scroll indicator at bottom of viewport. Hidden on mobile
          where the hero is content-tall and the affordance feels redundant. */}
      <div
        className="ow-fade-up hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        style={{ animationDelay: '2400ms' }}
      >
        <ScrollIndicator />
      </div>
    </section>
  );
}
